import { TooltipDirective } from '../../tooltip/tooltip.directive';
import { AfterViewInit, ContentChild, Directive, Input } from '@angular/core';

@Directive({
  selector: '[sag-ui-range-base]',
})
export class RangeBaseDirective implements AfterViewInit {
  private input!: HTMLInputElement;
  private thumb!: HTMLElement;
  private track!: HTMLElement;

  @Input('thumb')
  set rangeThumb(element: HTMLElement) {
    this.thumb = element;
    this.thumb.addEventListener('mousedown', this.onMouseDown.bind(this));
  }

  @Input('track')
  set rangeTrack(element: HTMLElement) {
    this.track = element;
    this.track.addEventListener('click', this.onClick.bind(this));
  }

  @Input('input')
  set rangeInput(element: HTMLElement) {
    this.input = element as HTMLInputElement;
    this.input.addEventListener('input', this.updateThumbPosition.bind(this));
  }

  @ContentChild(TooltipDirective) tooltip!: TooltipDirective;

  protected get min(): number {
    return this.input.min ? parseFloat(this.input.min) : 0;
  }

  protected get max(): number {
    return this.input.max ? parseFloat(this.input.max) : 100;
  }

  protected get step(): number {
    return this.input.step ? parseFloat(this.input.step) : 1;
  }

  protected get thumbPosition(): number {
    const percent =
      (this.input.valueAsNumber - this.min) / (this.max - this.min);
    return percent * 100;
  }

  ngAfterViewInit(): void {
    this.updateThumbPosition();
  }

  private updateThumbPosition(): void {
    this.thumb.style.left = `${this.thumbPosition}%`;
    if (this.tooltip) {
      this.tooltip.tooltip = this.input.value;
    }
  }

  protected onMouseDown(event: MouseEvent): void {
    if (this.input.disabled) return;

    const clientX = event.clientX;
    event.stopPropagation();

    const { left, width } = this.track.getBoundingClientRect();
    const x = clientX - left;
    const percent = Math.max(0, Math.min(1, x / width));
    const newValue = Math.round(this.min + percent * (this.max - this.min));
    const stepValue = Math.round(newValue / this.step) * this.step;
    this.input.valueAsNumber = stepValue;
    this.updateThumbPosition();

    const onMouseMove = (e: MouseEvent) => {
      if (this.input.disabled) return;

      const clientX = e.clientX;
      const x = clientX - left - 8;
      const percent = Math.max(0, Math.min(1, x / width));
      const newValue = Math.round(this.min + percent * (this.max - this.min));
      const stepValue = Math.round(newValue / this.step) * this.step;
      this.input.valueAsNumber = stepValue;
      this.updateThumbPosition();
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  protected onClick(event: MouseEvent): void {
    if (this.input.disabled) return;

    event.stopPropagation();

    const { left, width } = this.track.getBoundingClientRect();
    const x = event.clientX - left;
    const percent = Math.max(0, Math.min(1, x / width));
    const newValue = Math.round(this.min + percent * (this.max - this.min));
    const stepValue = Math.round(newValue / this.step) * this.step;
    this.input.valueAsNumber = stepValue;
    this.updateThumbPosition();
  }
}
